import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";
import MessageThread from "./MessageThread";

function tsToLabel(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Facility Admin only — every vendor company and every individual worker
// (both internal_staff and vendor_staff — a Facility Admin can message
// anyone at their own building) shows up as a thread. See
// MessageThread.jsx for the actual conversation view, reused here.
export default function MessagesPanel({ facilityId, vendors, senderName }) {
  const [workers, setWorkers] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [selected, setSelected] = useState(null); // { threadType, threadId, label }

  useEffect(() => onSnapshot(query(collection(db, "workers"), where("facilityId", "==", facilityId)), s =>
    setWorkers(s.docs.map(d => ({ id: d.id, ...d.data() })))), [facilityId]);

  useEffect(() => onSnapshot(query(collection(db, "messages"), where("facilityId", "==", facilityId), orderBy("timestamp", "asc")), s =>
    setAllMessages(s.docs.map(d => d.data()))), [facilityId]);

  const threads = [
    ...vendors.map(v => ({ threadType: "vendor", threadId: v.id, label: v.companyName, sub: v.trade })),
    ...workers.map(w => ({ threadType: "worker", threadId: w.id, label: w.name, sub: w.trade })),
  ].map(t => {
    const msgs = allMessages.filter(m => m.threadType === t.threadType && m.threadId === t.threadId);
    const last = msgs[msgs.length - 1];
    return { ...t, lastMessage: last, lastAt: last?.timestamp?.seconds || 0 };
  }).sort((a, b) => b.lastAt - a.lastAt);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 20, alignItems: "start" }} className="two-col-grid">
      <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        {threads.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>Add a vendor or worker to start messaging.</div>
        )}
        {threads.map((t, idx) => {
          const isSelected = selected?.threadType === t.threadType && selected?.threadId === t.threadId;
          return (
            <div key={`${t.threadType}:${t.threadId}`} onClick={() => setSelected(t)}
              style={{ padding: "14px 16px", cursor: "pointer", background: isSelected ? C.tintAccent : "transparent", borderBottom: idx === threads.length - 1 ? "none" : `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div style={{ fontWeight: 600 }}>{t.label}</div>
                {t.lastAt > 0 && <div style={{ fontSize: 11, color: C.muted }}>{tsToLabel(t.lastMessage.timestamp)}</div>}
              </div>
              <div style={{ fontSize: 12, color: C.muted }}>
                {t.lastMessage ? (t.lastMessage.text || (t.lastMessage.photoUrl ? "📷 Photo" : "")) : t.sub}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20, minHeight: 460 }}>
        {!selected ? (
          <div style={{ color: C.muted, fontSize: 13, textAlign: "center", paddingTop: 40 }}>Select a vendor or staff member to message them.</div>
        ) : (<>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12 }}>{selected.label}</div>
          <MessageThread facilityId={facilityId} threadType={selected.threadType} threadId={selected.threadId} senderRole="facility_admin" senderName={senderName || "Facility Admin"} />
        </>)}
      </div>
    </div>
  );
}
